import React, { memo, useCallback, useMemo, useRef, useState } from "react";

import AssignmentIcon from "@mui/icons-material/Assignment";
import EventIcon from "@mui/icons-material/Event";
import FlagIcon from "@mui/icons-material/Flag";
import Groups2Icon from "@mui/icons-material/Groups2";
import PersonIcon from "@mui/icons-material/Person";
import LoadingButton from "@mui/lab/LoadingButton";
import Box from "@mui/material/Box";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Grow from "@mui/material/Grow";
import Paper from "@mui/material/Paper";
import Popper from "@mui/material/Popper";
import Typography from "@mui/material/Typography";

import { useAppDispatch } from "app/hooks";
import {
  setCustomMessage,
  setOpen
} from "components/action-snackbar/action-snackbar-slice";
import {
  setOpen as setConfirmDialogOpen,
  setContent,
  setOnChange,
  setTitle
} from "components/confirm-dialog/confirm-dialog.slice";
import ErrorSnackbarContent from "components/error-snackbar-content";
import { useBulkUpdateTasksMutation } from "services/staff-tasks";
import type {
  StaffTasksFilter,
  UpdateBulkTaskRequest
} from "services/types/staff-tasks.type";

import BulkUpdateAssigneeModal from "./bulk-update-assignee-modal.component";
import BulkUpdateAssigneesModal from "./bulk-update-assignees-modal.component";
import BulkUpdateDueDateModal from "./bulk-update-due-date-modal.component";
import BulkUpdatePriorityModal from "./bulk-update-priority-modal.component";
import BulkUpdateStatusModal from "./bulk-update-status-modal.component";

type StaffTasksBulkActionsDropdownProps = {
  selectedTaskIds: number[];
  totalTasksCount: number;
  onTaskSelectionChange?: (taskIds: number[]) => void;
  onRefreshData?: () => void;
  isSelectAllMode?: boolean;
  totalFilteredCount?: number;
  currentFilters?: StaffTasksFilter;
};

const toIntArray = (value: unknown): number[] | undefined => {
  if (value == null) return undefined;

  if (Array.isArray(value)) {
    return value
      .map((v) => (typeof v === "string" ? parseInt(v, 10) : Number(v)))
      .filter((n) => !Number.isNaN(n));
  }

  if (typeof value === "number") {
    return [value];
  }

  if (typeof value === "string") {
    return value
      .split(",")
      .map((v) => parseInt(v.trim(), 10))
      .filter((n) => !Number.isNaN(n));
  }

  return undefined;
};

const adaptFiltersForBulkUpdate = (
  filters?: StaffTasksFilter
): UpdateBulkTaskRequest => {
  if (!filters) return {};

  const {
    assignee,
    created_by,
    patient,
    status,
    priority,
    created_from_bulk_action,
    is_active,
    is_complete,
    after_due_at,
    before_due_at
  } = filters;

  return {
    ...(assignee != null && { assignee: toIntArray(assignee) }),
    ...(created_by != null && { created_by: [created_by] }),
    ...(patient != null && { patient: toIntArray(patient) }),
    ...(status?.length && { status: toIntArray(status) }),
    ...(priority?.length && { priority: toIntArray(priority) }),
    ...(created_from_bulk_action !== undefined && { created_from_bulk_action }),
    ...(is_active !== undefined && { is_active }),
    ...(is_complete !== undefined && { is_complete }),
    ...(after_due_at && { after_due_at }),
    ...(before_due_at && { before_due_at })
  };
};

const StaffTasksBulkActionsDropdown = memo(
  ({
    selectedTaskIds,
    onTaskSelectionChange,
    onRefreshData,
    isSelectAllMode = false,
    totalFilteredCount = 0,
    currentFilters
  }: StaffTasksBulkActionsDropdownProps) => {
    const [popoverIsOpened, setPopoverIsOpened] = useState(false);
    const [showStatusModal, setShowStatusModal] = useState(false);
    const [showPriorityModal, setShowPriorityModal] = useState(false);
    const [showDueDateModal, setShowDueDateModal] = useState(false);
    const [showAssigneeModal, setShowAssigneeModal] = useState(false);
    const [showAssigneesModal, setShowAssigneesModal] = useState(false);

    const [bulkUpdateTasks, { isLoading: isBulkUpdating }] =
      useBulkUpdateTasksMutation();
    const dispatch = useAppDispatch();
    const anchorRef = useRef<any>(null);

    const selectedCount = isSelectAllMode
      ? totalFilteredCount
      : selectedTaskIds.length;

    const hasActiveFilters = useMemo(() => {
      if (!currentFilters) return false;

      const meaningfulFilters = [
        "assignee",
        "created_by",
        "patient",
        "status",
        "priority",
        "after_due_at",
        "before_due_at",
        "created_from_bulk_action"
      ];

      return meaningfulFilters.some((key) => {
        const value = currentFilters[key as keyof StaffTasksFilter];
        if (value == null) return false;
        if (Array.isArray(value)) return value.length > 0;
        return true;
      });
    }, [currentFilters]);

    const isDisabled =
      selectedCount === 0 || (isSelectAllMode && !hasActiveFilters);

    const handleToggle = useCallback(() => {
      setPopoverIsOpened((prevOpen) => !prevOpen);
    }, []);

    const handleClose = useCallback((event: Event) => {
      if (
        anchorRef.current &&
        anchorRef.current.contains(event.target as HTMLElement)
      ) {
        return;
      }
      setPopoverIsOpened(false);
    }, []);

    const handleBulkUpdate = useCallback(
      async (updateData: {
        update_status?: number;
        update_priority?: number;
        update_due_at?: string;
        update_assignee?: number;
        update_assignees?: number[];
      }) => {
        try {
          let payload: UpdateBulkTaskRequest = {
            ...updateData
          };

          if (isSelectAllMode) {
            const filterPayload: UpdateBulkTaskRequest = currentFilters
              ? adaptFiltersForBulkUpdate(currentFilters)
              : {};
            payload = {
              ...filterPayload,
              ...updateData
            };
          } else {
            payload.task_ids = selectedTaskIds;
          }

          const result = await bulkUpdateTasks(payload);

          if ("error" in result) {
            dispatch(
              setCustomMessage(<ErrorSnackbarContent response={result} />)
            );
            dispatch(setOpen(true));
          } else {
            onTaskSelectionChange?.([]);
            onRefreshData?.();
            dispatch(
              setCustomMessage(
                `Successfully updated ${result.data.updated_count} task${
                  result.data.updated_count !== 1 ? "s" : ""
                }!`
              )
            );
            dispatch(setOpen(true));
          }
        } catch (error) {
          dispatch(setCustomMessage("Failed to update tasks"));
          dispatch(setOpen(true));
        }
      },
      [
        bulkUpdateTasks,
        selectedTaskIds,
        dispatch,
        onTaskSelectionChange,
        onRefreshData,
        currentFilters,
        isSelectAllMode
      ]
    );

    const handleUpdateStatus = useCallback(
      (status: number) => {
        dispatch(setTitle("Confirm Bulk Update"));
        dispatch(
          setContent(
            `You are about to update ${selectedCount} task${selectedCount !== 1 ? "s" : ""}. Do you want to continue?`
          )
        );
        dispatch(
          setOnChange(() => {
            handleBulkUpdate({ update_status: status });
            setShowStatusModal(false);
          })
        );
        dispatch(setConfirmDialogOpen(true));
      },
      [handleBulkUpdate, selectedCount, dispatch]
    );

    const handleUpdatePriority = useCallback(
      (priority: number) => {
        dispatch(setTitle("Confirm Bulk Update"));
        dispatch(
          setContent(
            `You are about to update ${selectedCount} task${selectedCount !== 1 ? "s" : ""}. Do you want to continue?`
          )
        );
        dispatch(
          setOnChange(() => {
            handleBulkUpdate({ update_priority: priority });
            setShowPriorityModal(false);
          })
        );
        dispatch(setConfirmDialogOpen(true));
      },
      [handleBulkUpdate, selectedCount, dispatch]
    );

    const handleUpdateDueDate = useCallback(
      (dueDate: string | null) => {
        dispatch(setTitle("Confirm Bulk Update"));
        dispatch(
          setContent(
            `You are about to update ${selectedCount} task${selectedCount !== 1 ? "s" : ""}. Do you want to continue?`
          )
        );
        dispatch(
          setOnChange(() => {
            handleBulkUpdate({ update_due_at: dueDate || undefined });
            setShowDueDateModal(false);
          })
        );
        dispatch(setConfirmDialogOpen(true));
      },
      [handleBulkUpdate, selectedCount, dispatch]
    );

    const handleUpdateAssignee = useCallback(
      (assignee: number) => {
        dispatch(setTitle("Confirm Bulk Update"));
        dispatch(
          setContent(
            `You are about to update ${selectedCount} task${selectedCount !== 1 ? "s" : ""}. Do you want to continue?`
          )
        );
        dispatch(
          setOnChange(() => {
            handleBulkUpdate({ update_assignee: assignee });
            setShowAssigneeModal(false);
          })
        );
        dispatch(setConfirmDialogOpen(true));
      },
      [handleBulkUpdate, selectedCount, dispatch]
    );

    const handleUpdateAssignees = useCallback(
      (assignees: number[]) => {
        dispatch(setTitle("Confirm Bulk Update"));
        dispatch(
          setContent(
            `You are about to update ${selectedCount} task${selectedCount !== 1 ? "s" : ""}. Do you want to continue?`
          )
        );
        dispatch(
          setOnChange(() => {
            handleBulkUpdate({ update_assignees: assignees });
            setShowAssigneesModal(false);
          })
        );
        dispatch(setConfirmDialogOpen(true));
      },
      [handleBulkUpdate, selectedCount, dispatch]
    );

    const menuItems = useMemo(
      () => [
        {
          label: "Update Status",
          icon: AssignmentIcon,
          onClick: () => setShowStatusModal(true),
          iconColor: "#1D4ED8"
        },
        {
          label: "Update Priority",
          icon: FlagIcon,
          onClick: () => setShowPriorityModal(true),
          iconColor: "#F44336"
        },
        {
          label: "Update Due Date",
          icon: EventIcon,
          onClick: () => setShowDueDateModal(true),
          iconColor: "#059669"
        },
        {
          label: "Update Assignee",
          icon: PersonIcon,
          onClick: () => setShowAssigneeModal(true),
          iconColor: "#7C3AED"
        },
        {
          label: "Update Assignees",
          icon: Groups2Icon,
          onClick: () => setShowAssigneesModal(true),
          iconColor: "#7C3AED"
        }
      ],
      []
    );

    return (
      <>
        <LoadingButton
          onClick={handleToggle}
          variant="outlined"
          size="small"
          loading={isBulkUpdating}
          disabled={isDisabled}
          ref={anchorRef}
        >
          Bulk Actions{selectedCount > 0 && ` (${selectedCount})`}
        </LoadingButton>
        <Popper
          sx={{
            zIndex: 1300
          }}
          open={popoverIsOpened}
          anchorEl={anchorRef.current}
          role={undefined}
          transition
          disablePortal
        >
          {({ TransitionProps, placement }) => (
            <Grow
              {...TransitionProps}
              style={{
                transformOrigin:
                  placement === "bottom" ? "center top" : "center bottom"
              }}
            >
              <Paper
                sx={{
                  borderRadius: "12px",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
                  padding: "16px 20px",
                  backgroundColor: "#FFFFFF"
                }}
              >
                <ClickAwayListener onClickAway={handleClose}>
                  <Box
                    id="staff-tasks-bulk-actions-menu"
                    display="flex"
                    flexDirection="column"
                    gap="12px"
                  >
                    {menuItems.map((item, index) => {
                      const IconComponent = item.icon;
                      return (
                        <Box
                          key={`action_${index}`}
                          component="button"
                          onClick={() => {
                            item.onClick();
                            setPopoverIsOpened(false);
                          }}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                            width: "100%",
                            padding: 0,
                            border: "none",
                            background: "transparent",
                            cursor: "pointer",
                            "&:hover": {
                              opacity: 0.8
                            },
                            "&:focus": {
                              outline: "none"
                            }
                          }}
                        >
                          <IconComponent
                            sx={{
                              fontSize: "20px",
                              color: item.iconColor || "inherit",
                              flexShrink: 0,
                              alignSelf: item.description
                                ? "flex-start"
                                : "center",
                              mt: item.description ? "2px" : 0
                            }}
                          />
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: "2px"
                            }}
                          >
                            <Typography
                              sx={{
                                fontWeight: 500,
                                fontSize: "14px"
                              }}
                            >
                              {item.label}
                            </Typography>
                            {item.description && (
                              <Typography
                                sx={{
                                  fontSize: "11px",
                                  color: "text.secondary",
                                  fontStyle: "italic"
                                }}
                              >
                                {item.description}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </Popper>

        <BulkUpdateStatusModal
          isOpen={showStatusModal}
          selectedCount={selectedCount}
          onClose={() => setShowStatusModal(false)}
          onSave={handleUpdateStatus}
          isLoading={isBulkUpdating}
        />

        <BulkUpdatePriorityModal
          isOpen={showPriorityModal}
          selectedCount={selectedCount}
          onClose={() => setShowPriorityModal(false)}
          onSave={handleUpdatePriority}
          isLoading={isBulkUpdating}
        />

        <BulkUpdateDueDateModal
          isOpen={showDueDateModal}
          selectedCount={selectedCount}
          onClose={() => setShowDueDateModal(false)}
          onSave={handleUpdateDueDate}
          isLoading={isBulkUpdating}
        />

        <BulkUpdateAssigneeModal
          isOpen={showAssigneeModal}
          selectedCount={selectedCount}
          onClose={() => setShowAssigneeModal(false)}
          onSave={handleUpdateAssignee}
          isLoading={isBulkUpdating}
        />

        <BulkUpdateAssigneesModal
          isOpen={showAssigneesModal}
          selectedCount={selectedCount}
          onClose={() => setShowAssigneesModal(false)}
          onSave={handleUpdateAssignees}
          isLoading={isBulkUpdating}
        />
      </>
    );
  }
);

export default StaffTasksBulkActionsDropdown;
